import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, FolderPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import MenuImage from '../components/MenuImage';

const API = API_BASE_URL;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const UNVERIFIED_IMAGE = 'Could not verify the latest image state. Please refresh and try again.';
// Only these existing backend responses establish a pre-commit storage failure.
const SAFE_STORAGE_FAILURES = new Set([
  'Image storage is not enabled',
  'Image processing is busy; retry shortly',
  'Image storage is unavailable; retry',
]);

const Menu = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [contextError, setContextError] = useState('');
  const activeRestaurant = useRef('');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openCat, setOpenCat] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [form, setForm] = useState({
    name: '', name_ar: '', name_ms: '', description: '', price: '', category: '', category_id: '', 
    image_url: null, available: true, variants: []
  });
  const [catForm, setCatForm] = useState({ name: '', name_ar: '', name_ms: '' });
  const [imageFile, setImageFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const [imageRetry, setImageRetry] = useState(false);
  const [imageBlocked, setImageBlocked] = useState(false);
  const unverifiedItems = useRef(new Set());
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const saveOperation = useRef(null);
  const editorGeneration = useRef(0);
  const canManageImages = ['super_admin', 'company_admin', 'restaurant_manager'].includes(user?.role);

  useEffect(() => {
    if (!imageFile) { setLocalPreview(null); return; }
    const url = URL.createObjectURL(imageFile);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => () => {
    editorGeneration.current += 1;
    saveOperation.current?.abort();
  }, []);

  const resetImageEditor = () => {
    editorGeneration.current += 1;
    saveOperation.current?.abort();
    saveOperation.current = null;
    setSaving(false); setImageFile(null); setRemoveImage(false);
    setImageError(''); setImageRetry(false); setProgress(null);
    setImageBlocked(false);
  };

  const selectImage = (event) => {
    if (saving || imageBlocked) return;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) { setImageError('Choose JPEG, PNG or static WebP.'); return; }
    if (file.size > 8 * 1024 * 1024) { setImageError('Image must be 8 MiB or smaller.'); return; }
    setImageFile(file); setRemoveImage(false); setImageError('');
  };

  useEffect(() => {
    let cancelled = false;
    activeRestaurant.current = '';
    setRestaurantId('');
    setRestaurants([]);
    setItems([]);
    setCategories([]);
    if (!user) return;
    axios.get(`${API}/restaurants`, { withCredentials: true }).then(({ data }) => {
      if (cancelled) return;
      const authorized = data.filter(r => /^[a-f\d]{24}$/i.test(r.id));
      setRestaurants(authorized);
      const preferred = [user.restaurant_id, localStorage.getItem('restaurant_id')];
      const selected = preferred.find(id => authorized.some(r => r.id === id))
        || (authorized.length === 1 ? authorized[0].id : '');
      activeRestaurant.current = selected;
      setRestaurantId(selected);
      setContextError(selected ? '' : 'Select an authorized restaurant before adding menu items or categories.');
    }).catch(() => {
      if (!cancelled) setContextError('Unable to load your restaurants. Reload the page to try again.');
    });
    return () => { cancelled = true; activeRestaurant.current = ''; };
  }, [user]);

  useEffect(() => {
    if (restaurantId) fetchData(restaurantId);
    // Each selection loads its own menu; late responses are discarded below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const selectRestaurant = (id) => {
    if (!restaurants.some(r => r.id === id)) return;
    resetImageEditor();
    activeRestaurant.current = id;
    setRestaurantId(id);
    setContextError('');
    setItems([]);
    setCategories([]);
    setEditingItem(null);
    setOpenAdd(false);
    setOpenCat(false);
    setFilterCategory('all');
    setForm({ name: '', name_ar: '', name_ms: '', description: '', price: '', category: '', category_id: '', image_url: null, available: true, variants: [] });
    setCatForm({ name: '', name_ar: '', name_ms: '' });
  };

  const requireRestaurantId = () => {
    if (restaurantId && activeRestaurant.current === restaurantId && restaurants.some(r => r.id === restaurantId)) return restaurantId;
    toast.error('Select an authorized restaurant before adding menu items or categories.');
    return null;
  };

  const fetchData = async (targetRestaurantId) => {
    if (!targetRestaurantId || activeRestaurant.current !== targetRestaurantId) return;
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(
          `${API}/menu?restaurant_id=${targetRestaurantId}`,
          { withCredentials: true }
        ),
        axios.get(
          `${API}/menu/categories?restaurant_id=${targetRestaurantId}`,
          { withCredentials: true }
        )
      ]);
      if (activeRestaurant.current !== targetRestaurantId) return;
      setItems(itemsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async (retryImageOnly = false) => {
    if (saveOperation.current || imageBlocked || unverifiedItems.current.has(editingItem?.id)) return;
    const restaurant_id = requireRestaurantId();
    if (!restaurant_id) return;
    const generation = editorGeneration.current;
    const controller = new AbortController();
    saveOperation.current = controller;
    const stillCurrent = () => editorGeneration.current === generation && activeRestaurant.current === restaurant_id;
    setSaving(true); setImageError('');
    let savedItem = editingItem;
    let detailsSaved = retryImageOnly;
    try {
      if (!retryImageOnly) {
        const { image_url, ...fields } = form;
        const payload = { ...fields, price: parseFloat(form.price) };
        if (savedItem) {
          await axios.patch(`${API}/menu/${savedItem.id}`, payload, { withCredentials: true });
        } else {
          const response = await axios.post(`${API}/menu`, { ...payload, restaurant_id }, { withCredentials: true });
          savedItem = response.data;
          if (stillCurrent() && savedItem?.id) {
            // Creation is already committed even if the following image request fails.
            setItems(current => [...current.filter(item => item.id !== savedItem.id), savedItem]);
            setFilterCategory('all');
          }
        }
        if (!stillCurrent()) return;
        setEditingItem(savedItem);
        detailsSaved = true;
      }
      if (imageFile || removeImage) {
        if (!savedItem?.id) throw new Error('Saved item ID is missing');
        const url = `${API}/restaurants/${restaurant_id}/menu/${savedItem.id}/image`;
        const options = { withCredentials: true, signal: controller.signal };
        if (imageFile) {
          const data = new FormData();
          data.append('file', imageFile);
          setProgress(0);
          await axios.put(url, data, { ...options, onUploadProgress: event => {
            if (stillCurrent()) setProgress(event.total ? Math.round(event.loaded / event.total * 100) : null);
          } });
        } else {
          await axios.delete(url, options);
        }
      }
      if (!stillCurrent()) return;
      toast.success('Saved');
      setOpenAdd(false);
      setEditingItem(null);
      resetImageEditor();
      setForm({ name: '', name_ar: '', name_ms: '', description: '', price: '', category: '', category_id: '', image_url: null, available: true, variants: [] });
      await fetchData(restaurant_id);
    } catch (error) {
      if (!stillCurrent()) return;
      if (detailsSaved && (imageFile || removeImage)) {
        const status = error.response?.status;
        const knownPreCommitFailure = imageFile && status === 503
          && SAFE_STORAGE_FAILURES.has(error.response?.data?.detail);
        const rejectedBeforeMutation = [401, 403, 404, 413, 415, 422].includes(status);
        if (knownPreCommitFailure || rejectedBeforeMutation) {
          setImageRetry(true);
          setImageError(imageFile ? 'Item saved; image upload failed. Retry.' : 'Item saved; image removal failed. Retry.');
        } else {
          // Conflicts, unknown 5xx responses and lost network responses may hide
          // a committed mutation. Never resend the stale selection/removal.
          unverifiedItems.current.add(savedItem.id);
          setImageBlocked(true); setImageRetry(false);
          setImageFile(null); setLocalPreview(null); setRemoveImage(false);
          setImageError('Verifying the latest image state…');
          try {
            const { data } = await axios.get(`${API}/menu?restaurant_id=${restaurant_id}`, {
              withCredentials: true, signal: controller.signal,
            });
            if (!stillCurrent()) return;
            const authoritative = data.find(item => item.id === savedItem.id && item.restaurant_id === restaurant_id);
            if (!authoritative) throw new Error('Saved item unavailable');
            setItems(current => current.some(item => item.id === authoritative.id)
              ? current.map(item => item.id === authoritative.id ? authoritative : item)
              : [...current, authoritative]);
            setEditingItem(authoritative);
            setForm(current => ({ ...current, image_url: authoritative.image_url || null }));
            unverifiedItems.current.delete(savedItem.id);
            setImageBlocked(false);
            setImageError(status === 409
              ? 'The image changed elsewhere. The latest image has been reloaded.'
              : 'Image status is uncertain. The latest saved item state has been reloaded.');
          } catch {
            if (stillCurrent()) setImageError(UNVERIFIED_IMAGE);
          }
        }
      } else toast.error('Failed to save');
    } finally {
      if (saveOperation.current === controller) {
        saveOperation.current = null;
        setSaving(false); setProgress(null);
      }
    }
  };

  const handleAddCategory = async () => {
    try {
      const restaurant_id = requireRestaurantId();
      if (!restaurant_id) return;
      await axios.post(`${API}/menu/categories`, { ...catForm, restaurant_id }, { withCredentials: true });
      toast.success('Category added');
      setOpenCat(false);
      setCatForm({ name: '', name_ar: '', name_ms: '' });
      await fetchData(restaurant_id);
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    const restaurant_id = requireRestaurantId();
    if (!restaurant_id) return;
    try {
      await axios.delete(`${API}/menu/categories/${id}`, { withCredentials: true });
      toast.success('Category deleted');
      await fetchData(restaurant_id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleDelete = async (id) => {
    const restaurant_id = requireRestaurantId();
    if (!restaurant_id) return;
    try {
      await axios.delete(`${API}/menu/${id}`, { withCredentials: true });
      toast.success('Deleted');
      await fetchData(restaurant_id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const toggleAvailability = async (id, current) => {
    const restaurant_id = requireRestaurantId();
    if (!restaurant_id) return;
    try {
      await axios.patch(`${API}/menu/${id}`, { available: !current }, { withCredentials: true });
      await fetchData(restaurant_id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const startEdit = (item) => {
    resetImageEditor();
    if (unverifiedItems.current.has(item.id)) {
      setImageBlocked(true); setImageError(UNVERIFIED_IMAGE);
    }
    setEditingItem(item);
    setForm({
      name: item.name || '',
      name_ar: item.name_ar || '',
      name_ms: item.name_ms || '',
      description: item.description || '',
      price: item.price.toString(),
      category: item.category || '',
      category_id: item.category_id || '',
      image_url: item.image_url || null,
      available: item.available,
      variants: item.variants || []
    });
    setOpenAdd(true);
  };

  const addVariant = () => {
    setForm({ ...form, variants: [...form.variants, { name: '', price: 0 }] });
  };

  const updateVariant = (idx, field, value) => {
    const newVariants = [...form.variants];
    newVariants[idx] = { ...newVariants[idx], [field]: field === 'price' && value !== '' ? Number(value) : value };
    setForm({ ...form, variants: newVariants });
  };

  const convertLegacyVariant = (idx) => {
    const variant = form.variants[idx];
    const base = Number(form.price);
    const modifier = Number(variant.price_modifier);
    if (!Number.isFinite(base) || !Number.isFinite(modifier) || base < 0 || modifier < 0 || form.price === '') {
      toast.error('Enter valid base and legacy prices before converting');
      return;
    }
    const { price_modifier, ...rest } = variant;
    const price = (Math.round(base * 100) + Math.round(modifier * 100)) / 100;
    setForm({ ...form, variants: form.variants.map((v, i) => i === idx ? { ...rest, price } : v) });
  };

  const removeVariant = (idx) => {
    setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) });
  };

  const filteredItems = filterCategory === 'all' 
    ? items 
    : items.filter(i => i.category_id === filterCategory || i.category === filterCategory);

  return (
    <div className="space-y-6" data-testid="menu-page">
      <Label>Restaurant</Label>
      <Select value={restaurantId} onValueChange={selectRestaurant}>
        <SelectTrigger data-testid="menu-restaurant"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
        <SelectContent>
          {restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {contextError && <p role="alert">{contextError}</p>}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('menu')}
        </h1>
        <div className="flex gap-2">
          <Dialog open={openCat} onOpenChange={setOpenCat}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn">
                <FolderPlus className="h-4 w-4 me-2" /> Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name (English)</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name-en" /></div>
                <div><Label>Name (العربية)</Label><Input value={catForm.name_ar} onChange={(e) => setCatForm({ ...catForm, name_ar: e.target.value })} /></div>
                <div><Label>Name (Bahasa Melayu)</Label><Input value={catForm.name_ms} onChange={(e) => setCatForm({ ...catForm, name_ms: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={handleAddCategory} style={{ backgroundColor: '#E57200' }} data-testid="save-cat-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={openAdd} onOpenChange={(open) => { setOpenAdd(open); if (!open) { resetImageEditor(); setEditingItem(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                resetImageEditor(); setEditingItem(null);
                setForm({ name: '', name_ar: '', name_ms: '', description: '', price: '', category: '', category_id: '', image_url: null, available: true, variants: [] });
              }} style={{ backgroundColor: '#E57200' }} data-testid="add-menu-btn">
                <Plus className="h-4 w-4 me-2" /> {t('addMenuItem')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingItem ? 'Edit' : 'Add'} Menu Item</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label>Name (EN)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name-en" /></div>
                  <div><Label>Name (AR)</Label><Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
                  <div><Label>Name (MS)</Label><Input value={form.name_ms} onChange={(e) => setForm({ ...form, name_ms: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>{t('price')}</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-price" /></div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category_id} onValueChange={(v) => {
                      const cat = categories.find(c => c.id === v);
                      setForm({ ...form, category_id: v, category: cat?.name || '' });
                    }}>
                      <SelectTrigger data-testid="menu-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories.length === 0 ? (
                          <SelectItem value="none" disabled>Add categories first</SelectItem>
                        ) : (
                          categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <section aria-label="Menu image" className="space-y-2">
                  <Label>Image</Label>
                  <MenuImage src={removeImage ? null : localPreview || form.image_url} alt="Menu item preview" className="w-full h-40 rounded object-contain" />
                  {canManageImages && <>
                    <label className="inline-flex min-h-11 items-center rounded border px-4 cursor-pointer">
                      {localPreview || form.image_url ? 'Replace' : 'Upload Image'}
                      <input aria-label="Upload Image" data-testid="menu-image-file" className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" disabled={saving || imageBlocked} onChange={selectImage} />
                    </label>
                    {(imageFile || form.image_url) && !removeImage && <button type="button" className="min-h-11 px-4" disabled={saving || imageBlocked} data-testid="remove-menu-image" onClick={() => { setImageFile(null); setRemoveImage(true); setImageError(''); }}>Remove</button>}
                    <p className="text-xs text-muted-foreground">JPEG, PNG or static WebP. Maximum 8 MiB. Changes apply when you save.</p>
                  </>}
                  {saving && <p role="status">{progress === null ? 'Saving…' : progress >= 100 ? 'Processing image…' : `Uploading image… ${progress}%`}</p>}
                  {imageError && <p role="alert">{imageError}</p>}
                  {imageRetry && <button type="button" disabled={saving} onClick={() => handleSave(true)} data-testid="retry-menu-image">Retry image</button>}
                </section>
                
                {/* Variants */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Variants (Size/Options)</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-btn">
                      <Plus className="h-3 w-3 me-1" /> Add Variant
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Final selling prices replace the base price. Legacy extras remain additive until you convert them.</p>
                    {form.variants.map((v, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input placeholder="Variant name (e.g. Large)" value={v.name} onChange={(e) => updateVariant(idx, 'name', e.target.value)} data-testid={`variant-name-${idx}`} />
                        {'price_modifier' in v ? (
                          <div className="text-xs" data-testid={`legacy-variant-${idx}`}>
                            {'price' in v ? <span>Conflicting price fields: remove and recreate this variant.</span> : <>
                              <span>Legacy extra: +{v.price_modifier} to base price</span>
                              <Button type="button" size="sm" variant="outline" onClick={() => convertLegacyVariant(idx)} data-testid={`convert-variant-${idx}`}>Convert to final price</Button>
                            </>}
                          </div>
                        ) : (
                          <div>
                            <Label htmlFor={`variant-final-price-${idx}`}>Final selling price</Label>
                            <Input id={`variant-final-price-${idx}`} type="number" min="0" step="0.01" placeholder="Final selling price" value={v.price ?? ''} onChange={(e) => updateVariant(idx, 'price', e.target.value)} className="w-32" data-testid={`variant-price-${idx}`} />
                          </div>
                        )}
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeVariant(idx)} data-testid={`remove-variant-${idx}`}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch checked={form.available} onCheckedChange={(checked) => setForm({ ...form, available: checked })} />
                  <Label>{t('available')}</Label>
                </div>
              </div>
              <DialogFooter><Button disabled={saving || imageBlocked} onClick={() => handleSave(false)} style={{ backgroundColor: '#E57200' }} data-testid="save-menu-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <Tabs value={filterCategory} onValueChange={setFilterCategory}>
        <TabsList className="flex flex-wrap h-auto" data-testid="category-tabs">
          <TabsTrigger value="all" data-testid="cat-tab-all">All Items</TabsTrigger>
          {categories.map(c => (
            <TabsTrigger key={c.id} value={c.id} data-testid={`cat-tab-${c.id}`}>
              <span>{c.name}</span>
              <span
                role="button"
                tabIndex={0}
                aria-label="Delete category"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteCategory(c.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); handleDeleteCategory(c.id); } }}
                className="ms-2 inline-flex items-center justify-center h-4 w-4 rounded hover:bg-destructive/20 cursor-pointer"
                data-testid={`delete-cat-${c.id}`}
              >
                <X className="h-3 w-3" />
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredItems.length === 0 ? (
          <p className="text-center text-muted-foreground col-span-full py-12" style={{ fontFamily: 'Figtree, sans-serif' }}>
            No menu items in this category yet.
          </p>
        ) : (
          filteredItems.map(item => (
            <Card key={item.id} className="overflow-hidden border-border/40" data-testid={`menu-item-${item.id}`}>
              <div className="aspect-video bg-muted overflow-hidden">
                <MenuImage src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.name}</h3>
                    <p className="text-xs text-muted-foreground">{item.category}</p>
                  </div>
                  <Badge style={{ backgroundColor: item.available ? '#10B981' : '#64748B', color: 'white' }}>
                    {item.available ? t('available') : t('unavailable')}
                  </Badge>
                </div>
                <div className="text-xl font-bold" style={{ color: '#E57200', fontFamily: 'Outfit, sans-serif' }}>
  RM {item.price.toFixed(2)}
</div>
                {item.variants && item.variants.length > 0 && (
                  <div className="text-xs text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {item.variants.length} variant{item.variants.length > 1 ? 's' : ''} available
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(item)} data-testid={`edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => toggleAvailability(item.id, item.available)} data-testid={`toggle-${item.id}`}>
                    {item.available ? 'Disable' : 'Enable'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Menu;
